import { Product, PaginatedResponse, PaginationParams, Category } from '../types';
import { apiClient } from './api';

type PagePayload<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function unwrapPage<T>(res: { success: boolean; data?: PagePayload<T>; message?: string; error?: string }): PaginatedResponse<T> {
  if (!res.success || !res.data) {
    throw new Error(res.message || res.error || 'خطا در دریافت داده');
  }
  return {
    data: res.data.data ?? [],
    total: res.data.total ?? 0,
    page: res.data.page ?? 1,
    limit: res.data.limit ?? 10,
    totalPages: res.data.totalPages ?? 1,
  };
}

function unwrapData<T>(res: { success: boolean; data?: T; message?: string; error?: string }, msg: string): T {
  if (!res.success || res.data === undefined) throw new Error(res.message || res.error || msg);
  return res.data;
}

export type GalleryImage = {
  id: string;
  url: string;
  thumbnailUrl?: string;
  alt: string;
  isMain: boolean;
  sortOrder?: number;
  width?: number;
  height?: number;
};

function apiBase(): string {
  return (import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1').replace(/\/$/, '');
}

async function multipartUpload<T>(path: string, form: FormData): Promise<T> {
  const token = localStorage.getItem('adminToken');
  const res = await fetch(`${apiBase()}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (res.status === 413) {
    throw new Error(
      'حجم فایل زیاد است (محدودیت سرور/nginx). client_max_body_size را روی api افزایش دهید یا GIF کوچک‌تری بسازید.',
    );
  }
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.success) {
    throw new Error(data?.message || data?.error || 'آپلود ناموفق بود');
  }
  return data.data as T;
}

export const productService = {
  async getProducts(
    params: PaginationParams & { q?: string; categoryId?: string },
  ): Promise<PaginatedResponse<Product>> {
    const res = await apiClient.get<PagePayload<Product>>('/admin/products', {
      params: {
        page: params.page,
        limit: params.limit,
        sort: params.sortBy || undefined,
        q: params.q || undefined,
        categoryId: params.categoryId || undefined,
      },
    });
    return unwrapPage(res);
  },

  async getProduct(id: string): Promise<Product> {
    const res = await apiClient.get<Product>(`/admin/products/${id}`);
    return unwrapData(res, 'محصول یافت نشد');
  },

  async createProduct(data: Record<string, unknown>): Promise<Product> {
    const res = await apiClient.post<Product>('/admin/products', data);
    return unwrapData(res, 'ایجاد محصول ناموفق بود');
  },

  async updateProduct(id: string, data: Record<string, unknown>): Promise<Product> {
    const res = await apiClient.put<Product>(`/admin/products/${id}`, data);
    return unwrapData(res, 'به‌روزرسانی ناموفق بود');
  },

  async deleteProduct(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/products/${id}`);
    if (!res.success) throw new Error(res.message || 'حذف ناموفق بود');
  },

  async getCategories(): Promise<Category[]> {
    const res = await apiClient.get<Category[]>('/admin/categories');
    return unwrapData(res, 'خطا در دریافت دسته‌ها');
  },

  async createCategory(data: Record<string, unknown>): Promise<Category> {
    const res = await apiClient.post<Category>('/admin/categories', data);
    return unwrapData(res, 'ایجاد دسته ناموفق بود');
  },

  async updateCategory(id: string, data: Record<string, unknown>): Promise<Category> {
    const res = await apiClient.put<Category>(`/admin/categories/${id}`, data);
    return unwrapData(res, 'ویرایش دسته ناموفق بود');
  },

  async deleteCategory(id: string): Promise<void> {
    const res = await apiClient.delete(`/admin/categories/${id}`);
    if (!res.success) throw new Error(res.message || 'حذف دسته ناموفق بود');
  },

  async listImages(productId: string): Promise<GalleryImage[]> {
    const res = await apiClient.get<GalleryImage[]>(`/admin/products/${productId}/images`);
    return unwrapData(res, 'خطا در دریافت گالری');
  },

  async uploadImage(
    productId: string,
    file: File,
    opts?: { alt?: string; isMain?: boolean }
  ): Promise<GalleryImage> {
    const form = new FormData();
    form.append('file', file);
    if (opts?.alt) form.append('alt', opts.alt);
    if (opts?.isMain) form.append('isMain', 'true');
    return multipartUpload<GalleryImage>(`/admin/products/${productId}/images`, form);
  },

  async patchImage(
    productId: string,
    imageId: string,
    data: { alt?: string; isMain?: boolean }
  ): Promise<GalleryImage[]> {
    const res = await apiClient.patch<GalleryImage[]>(`/admin/products/${productId}/images/${imageId}`, data);
    return unwrapData(res, 'ویرایش تصویر ناموفق بود');
  },

  async deleteImage(productId: string, imageId: string): Promise<void> {
    const res = await apiClient.delete(`/admin/products/${productId}/images/${imageId}`);
    if (!res.success) throw new Error(res.message || 'حذف تصویر ناموفق بود');
  },

  async reorderImages(productId: string, imageIds: string[]): Promise<GalleryImage[]> {
    const res = await apiClient.put<GalleryImage[]>(`/admin/products/${productId}/images/reorder`, {
      imageIds,
    });
    return unwrapData(res, 'مرتب‌سازی ناموفق بود');
  },

  async uploadFile(file: File): Promise<{ url: string; thumbnailUrl?: string }> {
    const form = new FormData();
    form.append('file', file);
    return multipartUpload<{ url: string; thumbnailUrl?: string }>('/admin/uploads', form);
  },
};
